import { Chat } from "@/db/schema";
import { useChatVisibility } from "@/hooks/use-chat-visibility";
import Link from "next/link";
import { memo, useState } from "react";

import {
    CheckCircleFillIcon,
    CopyIcon,
    GlobeIcon,
    LockIcon,
    MoreHorizontalIcon,
    ShareIcon,
    TrashIcon,
} from "./icons";
import { Button } from "./ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import {
    SidebarMenuAction,
    SidebarMenuButton,
    SidebarMenuItem,
} from "./ui/sidebar";

const PureChatItem = ({
    chat,
    isActive,
    onDelete,
    setOpenMobile,
}: {
    chat: Chat;
    isActive: boolean;
    onDelete: (chatId: string) => void;
    setOpenMobile: (open: boolean) => void;
}) => {
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [madePublic, setMadePublic] = useState(false);
    const { visibilityType, setVisibilityType } = useChatVisibility({
        chatId: chat.id,
        initialVisibility: chat.visibility,
    });

    // Reset states when dialog opens/closes
    const handleDialogChange = (open: boolean) => {
        setShareDialogOpen(open);
        if (!open) {
            setMadePublic(false);
            setCopied(false);
        } else {
            // If chat is already public, set madePublic to true
            setMadePublic(visibilityType === "public");
        }
    };

    const shareUrl =
        typeof window !== "undefined"
            ? `${window.location.origin}/intelligence/chat/${chat.id}`
            : `/intelligence/chat/${chat.id}`;

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleShare = () => {
        if (madePublic) {
            // If already made public, close the dialog
            setShareDialogOpen(false);
        } else {
            // Make the chat public
            setVisibilityType("public");
            setMadePublic(true);
        }
    };

    return (
        <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive}>
                <Link
                    href={`/intelligence/chat/${chat.id}`}
                    onClick={() => setOpenMobile(false)}
                >
                    <span>{chat.title}</span>
                </Link>
            </SidebarMenuButton>

            <DropdownMenu modal={true}>
                <DropdownMenuTrigger asChild>
                    <SidebarMenuAction
                        className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground mr-0.5"
                        showOnHover={!isActive}
                    >
                        <MoreHorizontalIcon />
                        <span className="sr-only">More</span>
                    </SidebarMenuAction>
                </DropdownMenuTrigger>

                <DropdownMenuContent side="bottom" align="end">
                    <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => setShareDialogOpen(true)}
                    >
                        <ShareIcon />
                        <span>Share</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem
                        className="cursor-pointer text-destructive focus:bg-destructive/15 focus:text-destructive dark:text-red-500"
                        onSelect={() => onDelete(chat.id)}
                    >
                        <TrashIcon />
                        <span>Delete</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={shareDialogOpen} onOpenChange={handleDialogChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Share Chat</DialogTitle>
                        <DialogDescription>
                            {madePublic
                                ? "Your chat is now public. Anyone with the link can view it."
                                : "This will make your chat public and anyone with the link will be able to view it."}
                        </DialogDescription>
                    </DialogHeader>
                    {madePublic && (
                        <div className="flex items-center space-x-2 mt-4">
                            <Input
                                value={shareUrl}
                                readOnly
                                className="flex-1"
                            />
                            <Button
                                onClick={handleCopyUrl}
                                variant="outline"
                                className="flex-shrink-0"
                            >
                                {copied ? "Copied!" : "Copy"}
                                <span className="ml-2">
                                    <CopyIcon />
                                </span>
                            </Button>
                        </div>
                    )}
                    {!madePublic && (
                        <DialogFooter className="w-full">
                            <Button className="w-full" onClick={handleShare}>
                                Make Public
                            </Button>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>
        </SidebarMenuItem>
    );
};

export const ChatItem = memo(PureChatItem, (prevProps, nextProps) => {
    if (prevProps.isActive !== nextProps.isActive) return false;
    return true;
});
